import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { toast } from 'react-toastify';
import QRCode from 'qrcode';
import protheusPaymentsService from '../../../services/protheusPaymentsService';

const parseDateStr = (dateStr) => {
  const raw = String(dateStr || '').trim();
  if (!raw) return null;
  let y;
  let m;
  let d;
  if (raw.includes('-')) {
    const parts = raw.split('T')[0].split('-');
    y = parts[0];
    m = parts[1];
    d = parts[2];
  } else if (raw.includes('/')) {
    const parts = raw.split('/');
    d = parts[0];
    m = parts[1];
    y = parts[2];
  } else if (raw.length === 8) {
    y = raw.substring(0, 4);
    m = raw.substring(4, 6);
    d = raw.substring(6, 8);
  } else {
    return null;
  }
  const dt = new Date(Number(y), Number(m) - 1, Number(d));
  if (Number.isNaN(dt.getTime())) return null;
  return dt;
};

const formatDateDisplay = (date) => {
  const dt = date instanceof Date ? date : null;
  if (!dt) return '—';
  const d = String(dt.getDate()).padStart(2, '0');
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const y = String(dt.getFullYear());
  return `${d}/${m}/${y}`;
};

const parseMoney = (value) => {
  let s = String(value ?? '').trim();
  if (!s) return 0;
  if (s.includes(',')) s = s.replace(/\./g, '').replace(',', '.');
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
};

const formatMoney = (value) => {
  const n = Number(value || 0);
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number.isFinite(n) ? n : 0);
};

const extractPixCode = (data) => {
  const d = data || {};
  if (d._messages && Array.isArray(d._messages) && d._messages.length > 0) {
    const first = d._messages[0] || {};
    if (first.Chave) return String(first.Chave);
    if (first.CHAVE_PIX) return String(first.CHAVE_PIX);
  }
  return String(d.Chave || d.CHAVE_PIX || d.qrcode || d.brcode || d.pix_copy_paste || '');
};

const PixDialog = ({ open, onClose, pixCode, totalValue }) => {
  const canvasRef = useRef(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (!pixCode) return;
    if (!canvasRef.current) return;
    try {
      QRCode.toCanvas(canvasRef.current, pixCode, {
        width: 240,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });
    } catch (_) {}
  }, [open, pixCode]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(pixCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Falha ao copiar');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Pagamento via PIX</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" alignItems="center" gap={2} py={1}>
          <Typography variant="body2" color="text.secondary">
            Valor: {formatMoney(totalValue)}
          </Typography>
          <canvas ref={canvasRef} />
          <TextField value={pixCode} fullWidth multiline minRows={3} InputProps={{ readOnly: true }} />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>Fechar</Button>
        <Button variant="contained" onClick={copy} disabled={!pixCode}>
          {copied ? 'Copiado' : 'Copiar código'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const OpenPixKeysDialog = ({ open, onClose, keys }) => {
  const copy = async (value) => {
    try {
      await navigator.clipboard.writeText(String(value || ''));
      toast.success('Copiado');
    } catch {
      toast.error('Falha ao copiar');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>PIX em aberto</DialogTitle>
      <DialogContent>
        {(!keys || keys.length === 0) ? (
          <Typography variant="body2" color="text.secondary">Nenhuma chave encontrada</Typography>
        ) : (
          <Box display="grid" gap={2} py={1}>
            {keys.map((k, idx) => (
              <Paper key={`${k?.FILIAL || 'x'}-${idx}`} variant="outlined" sx={{ p: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
                  <Box>
                    <Typography variant="subtitle2">Filial: {String(k?.FILIAL || '-')}</Typography>
                    <Typography variant="body2" color="text.secondary">Valor: {String(k?.VALOR || '-')}</Typography>
                  </Box>
                  <Button variant="outlined" onClick={() => copy(k?.CHAVE_PIX || '')}>
                    Copiar
                  </Button>
                </Box>
                <Box mt={1}>
                  <TextField value={String(k?.CHAVE_PIX || '')} fullWidth multiline minRows={2} InputProps={{ readOnly: true }} />
                </Box>
              </Paper>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
};

const PagamentosPage = () => {
  const [cpf, setCpf] = useState('');
  const [loading, setLoading] = useState(false);
  const [clientData, setClientData] = useState(null);

  const [stores, setStores] = useState([]);
  const [parcelsByStore, setParcelsByStore] = useState({});
  const [clientName, setClientName] = useState('Cliente');
  const [clientCode, setClientCode] = useState('');
  const [selectedStoreId, setSelectedStoreId] = useState(null);
  const [selectedParcelIds, setSelectedParcelIds] = useState([]);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pixDialogOpen, setPixDialogOpen] = useState(false);
  const [pixCode, setPixCode] = useState('');
  const [pixTotal, setPixTotal] = useState(0);
  const [generatingPix, setGeneratingPix] = useState(false);

  const [openPixDialogOpen, setOpenPixDialogOpen] = useState(false);
  const [openPixKeys, setOpenPixKeys] = useState([]);

  const [enforceSequential, setEnforceSequential] = useState(true);

  const selectedParcels = useMemo(() => {
    if (!selectedStoreId) return [];
    const storeParcels = parcelsByStore[selectedStoreId] || [];
    const selected = new Set(selectedParcelIds);
    return storeParcels.filter(p => selected.has(p.id));
  }, [parcelsByStore, selectedParcelIds, selectedStoreId]);

  const totals = useMemo(() => {
    const totalValue = selectedParcels.reduce((acc, p) => acc + (Number(p.value) || 0), 0);
    const totalInterest = selectedParcels.reduce((acc, p) => acc + (Number(p.interest) || 0), 0);
    return { totalValue, totalInterest };
  }, [selectedParcels]);

  const processClientData = useCallback((data, usedCpf) => {
    const d = data || {};
    setClientData(d);
    setCpf(String(usedCpf || '').trim());
    setClientName(String(d.NOME || d.CLIENTE || d.nome || 'Cliente'));
    setClientCode(String(d.CODIGO || d.codigo || ''));

    const rawTitulos = d.TITULOS || d.titulos || d.ITEMS || d.items || [];
    const openPixStoresString = String(d.LOJAS_PIX_GERADO || '');
    const openPixStores = openPixStoresString.split('|').map(s => s.trim()).filter(Boolean);
    const rawKeys = Array.isArray(d.CHAVES) ? d.CHAVES : [];
    const processedKeys = rawKeys
      .filter(k => typeof k === 'object' && k !== null)
      .map(k => ({
        FILIAL: String(k.FILIAL || ''),
        VALOR: String(k.VALOR || ''),
        CHAVE_PIX: String(k.CHAVE_PIX || '')
      }));
    setOpenPixKeys(processedKeys);

    const storesMap = new Map();
    const parcelsMap = {};
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    (Array.isArray(rawTitulos) ? rawTitulos : []).forEach((titulo, index) => {
      const rawStoreString = String(titulo?.FILIAL || titulo?.LOJA || titulo?.nome_loja || 'Loja 01 - COMETA');
      const parts = rawStoreString.split(' - ');
      const rawCode = String(parts[0] || '').trim();
      const codeMatch = rawCode.match(/(\d+)/);
      const filialCode = codeMatch ? codeMatch[0] : rawCode;
      const storeName = filialCode ? `Loja ${filialCode}` : rawStoreString;
      const storeAddress = (parts.length > 2 ? parts.slice(2).join(' - ') : (titulo?.ENDERECO || titulo?.endereco || '')) || '';
      const storeId = filialCode ? `store-${filialCode}` : `store-${index}`;

      if (!storesMap.has(storeId)) {
        const hasOpenPix = openPixStores.some(s => s === filialCode || Number(s) === Number(filialCode || 0));
        storesMap.set(storeId, {
          id: storeId,
          name: storeName,
          address: storeAddress,
          filialCode: filialCode,
          parcelsCount: 0,
          totalValue: 0,
          hasOpenPix
        });
        parcelsMap[storeId] = [];
      }

      const store = storesMap.get(storeId);
      const dateObj = parseDateStr(titulo?.DT_VENCTO || titulo?.DATA_DE_VENCIMENTO || titulo?.vencimento || '');
      const isLate = dateObj ? dateObj < today : false;
      const value = parseMoney(titulo?.VALOR || titulo?.SALDO_A_PAGAR || titulo?.saldo || '0');
      const interest = parseMoney(titulo?.JUROS || titulo?.JUROS_A_PAGAR || '0');

      const parcela = String(titulo?.PARCELA || titulo?.parcela || '').trim() || '01';
      const parcel = {
        id: String(titulo?.SEU_NUMERO || titulo?.ID || titulo?.id || `p-${index}`),
        parcela,
        dueDate: formatDateDisplay(dateObj),
        dueTs: dateObj ? dateObj.getTime() : 0,
        value,
        interest,
        status: isLate ? 'vencida' : 'em_dia',
        prefix: String(titulo?.PREFIXO || ''),
        contractNumber: String(titulo?.NUMERO || ''),
        type: String(titulo?.TIPO || ''),
        filial: String(titulo?.FILIAL || filialCode || '')
      };

      parcelsMap[storeId].push(parcel);
      store.parcelsCount += 1;
      store.totalValue += value;
    });

    Object.keys(parcelsMap).forEach(storeId => {
      parcelsMap[storeId].sort((a, b) => (a.dueTs || 0) - (b.dueTs || 0));
    });

    const sortedStores = Array.from(storesMap.values()).sort((a, b) => String(a.filialCode || '').localeCompare(String(b.filialCode || '')));
    setStores(sortedStores);
    setParcelsByStore(parcelsMap);
    const firstStore = sortedStores[0]?.id || null;
    setSelectedStoreId(firstStore);
    setSelectedParcelIds([]);
  }, []);

  const search = useCallback(async () => {
    const cleanCpf = String(cpf || '').replace(/\D/g, '').trim();
    if (!cleanCpf) {
      toast.error('Informe o CPF');
      return;
    }
    setLoading(true);
    try {
      const resp = await protheusPaymentsService.getClientData(cleanCpf);
      processClientData(resp, cleanCpf);
    } catch (e) {
      toast.error(e?.message || 'Falha ao consultar');
      setClientData(null);
      setStores([]);
      setParcelsByStore({});
      setSelectedStoreId(null);
      setSelectedParcelIds([]);
    } finally {
      setLoading(false);
    }
  }, [cpf, processClientData]);

  const storeParcels = useMemo(() => {
    if (!selectedStoreId) return [];
    return parcelsByStore[selectedStoreId] || [];
  }, [parcelsByStore, selectedStoreId]);

  const toggleParcel = (parcelId) => {
    if (!selectedStoreId) return;
    const storeParcelsLocal = parcelsByStore[selectedStoreId] || [];
    const target = storeParcelsLocal.find(p => p.id === parcelId);
    if (!target) return;

    const isSelecting = !selectedParcelIds.includes(parcelId);
    if (enforceSequential && isSelecting) {
      const targetTs = Number(target.dueTs || 0);
      const selectedSet = new Set(selectedParcelIds);
      const hasUnselectedEarlier = storeParcelsLocal.some(p => (Number(p.dueTs || 0) < targetTs) && !selectedSet.has(p.id));
      if (hasUnselectedEarlier) {
        toast.error('A seleção deve ser sequencial (da menor para a maior data de vencimento).');
        return;
      }
    }

    setSelectedParcelIds(prev => {
      const set = new Set(prev);
      if (set.has(parcelId)) set.delete(parcelId);
      else set.add(parcelId);
      return Array.from(set);
    });
  };

  const openPixKeysForSelectedStore = () => {
    const store = stores.find(s => s.id === selectedStoreId) || null;
    if (!store?.filialCode) {
      toast.error('Filial não identificada');
      return;
    }
    const keys = (openPixKeys || []).filter(k => String(k.FILIAL || '').trim() === String(store.filialCode || '').trim());
    setOpenPixDialogOpen(true);
    setOpenPixKeys(keys);
  };

  const canGenerate = selectedParcels.length > 0 && !generatingPix;

  const doGeneratePix = async () => {
    const store = stores.find(s => s.id === selectedStoreId) || null;
    if (!store?.filialCode) {
      toast.error('Selecione uma loja válida');
      return;
    }
    if (selectedParcels.length === 0) {
      toast.error('Selecione parcelas');
      return;
    }

    setGeneratingPix(true);
    try {
      const cleanCpf = String(cpf || '').replace(/\D/g, '').trim();
      const items = selectedParcels.map(p => ({
        PREFIXO: String(p.prefix || ''),
        NUMERO: String(p.contractNumber || ''),
        PARCELA: String(p.parcela || ''),
        TIPO: String(p.type || ''),
        VALOR: Number(p.value || 0).toFixed(2),
        JUROS: Number(p.interest || 0).toFixed(2)
      }));

      const body = {
        FILIAL: String(store.filialCode),
        CODIGO: String(clientCode || ''),
        NOME: String(clientName || ''),
        CPF: cleanCpf,
        TITULOS: items,
        TOTAIS: [
          {
            VALOR_TOTAL: Number(totals.totalValue || 0).toFixed(2),
            TOTAL_JUROS: Number(totals.totalInterest || 0).toFixed(2)
          }
        ]
      };

      const resp = await protheusPaymentsService.generatePix(cleanCpf, body);
      const code = extractPixCode(resp);
      if (!code) {
        toast.error('PIX gerado, mas não retornou a chave');
        return;
      }
      setPixCode(code);
      setPixTotal(Number(totals.totalValue || 0) + Number(totals.totalInterest || 0));
      setPixDialogOpen(true);
      toast.success('PIX gerado com sucesso');
    } catch (e) {
      toast.error(e?.message || 'Falha ao gerar PIX');
    } finally {
      setGeneratingPix(false);
    }
  };

  useEffect(() => {
    if (!selectedStoreId) return;
    setSelectedParcelIds([]);
  }, [selectedStoreId]);

  const selectedStore = useMemo(() => stores.find(s => s.id === selectedStoreId) || null, [stores, selectedStoreId]);
  const hasOpenPixForSelectedStore = Boolean(selectedStore?.hasOpenPix);

  return (
    <Box p={3}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h5">Pagamentos</Typography>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              label="CPF"
              value={cpf}
              onChange={(e) => setCpf(e.target.value)}
              fullWidth
              placeholder="000.000.000-00"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Button variant="contained" onClick={search} disabled={loading} fullWidth>
              {loading ? <CircularProgress size={20} /> : 'Consultar'}
            </Button>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControlLabel
              control={<Switch checked={enforceSequential} onChange={(e) => setEnforceSequential(e.target.checked)} />}
              label="Seleção sequencial"
            />
          </Grid>
        </Grid>
        {!!clientData && (
          <Box mt={2}>
            <Typography variant="subtitle1">{clientName}</Typography>
            <Typography variant="body2" color="text.secondary">Código: {clientCode || '—'}</Typography>
          </Box>
        )}
      </Paper>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1">Lojas</Typography>
            <Divider sx={{ my: 1 }} />
            {stores.length === 0 ? (
              <Typography variant="body2" color="text.secondary">Nenhuma loja</Typography>
            ) : (
              <List dense>
                {stores.map(s => (
                  <ListItemButton
                    key={s.id}
                    selected={s.id === selectedStoreId}
                    onClick={() => setSelectedStoreId(s.id)}
                  >
                    <ListItemText
                      primary={`${s.name} (${s.parcelsCount})`}
                      secondary={`${formatMoney(s.totalValue)}${s.hasOpenPix ? ' · PIX em aberto' : ''}`}
                    />
                  </ListItemButton>
                ))}
              </List>
            )}
            <Box mt={2} display="flex" gap={1}>
              <Button
                variant="outlined"
                onClick={openPixKeysForSelectedStore}
                disabled={!hasOpenPixForSelectedStore}
                fullWidth
              >
                PIX em aberto
              </Button>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="subtitle1">Parcelas</Typography>
              <Typography variant="body2" color="text.secondary">
                Selecionadas: {selectedParcelIds.length} · Total: {formatMoney(totals.totalValue + totals.totalInterest)}
              </Typography>
            </Box>
            <Divider sx={{ mb: 1 }} />
            {storeParcels.length === 0 ? (
              <Typography variant="body2" color="text.secondary">Nenhuma parcela</Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={56}></TableCell>
                    <TableCell>Parcela</TableCell>
                    <TableCell>Vencimento</TableCell>
                    <TableCell align="right">Valor</TableCell>
                    <TableCell align="right">Juros</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {storeParcels.map(p => {
                    const checked = selectedParcelIds.includes(p.id);
                    return (
                      <TableRow key={p.id} hover onClick={() => toggleParcel(p.id)} style={{ cursor: 'pointer' }}>
                        <TableCell padding="checkbox">
                          <input type="checkbox" checked={checked} readOnly />
                        </TableCell>
                        <TableCell>{p.parcela}</TableCell>
                        <TableCell>{p.dueDate}</TableCell>
                        <TableCell align="right">{formatMoney(p.value)}</TableCell>
                        <TableCell align="right">{formatMoney(p.interest)}</TableCell>
                        <TableCell>{p.status === 'vencida' ? 'Vencida' : 'Em dia'}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
            <Box mt={2} display="flex" justifyContent="flex-end" gap={1}>
              <Button variant="outlined" onClick={() => setSelectedParcelIds([])} disabled={selectedParcelIds.length === 0}>
                Limpar
              </Button>
              <Button variant="contained" onClick={() => setConfirmOpen(true)} disabled={!canGenerate}>
                {generatingPix ? 'Gerando...' : 'Gerar PIX'}
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirmar geração de PIX</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Loja: {selectedStore?.name || '—'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Parcelas: {selectedParcels.length}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Total: {formatMoney(totals.totalValue + totals.totalInterest)}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setConfirmOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={async () => {
              setConfirmOpen(false);
              await doGeneratePix();
            }}
            disabled={!canGenerate}
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      <PixDialog
        open={pixDialogOpen}
        onClose={() => setPixDialogOpen(false)}
        pixCode={pixCode}
        totalValue={pixTotal}
      />

      <OpenPixKeysDialog
        open={openPixDialogOpen}
        onClose={() => setOpenPixDialogOpen(false)}
        keys={openPixKeys}
      />
    </Box>
  );
};

export default PagamentosPage;

